import React, { useState, useMemo, useEffect } from 'react';
import { Deal, Trade, AccountType } from '../types';

interface DealsProps {
  deals: Deal[];
  totalNetworkPayout: number;
}

const ITEMS_PER_PAGE = 10;

const Deals: React.FC<DealsProps> = ({ deals, totalNetworkPayout }) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Memoize the selected deal
  const selectedDeal = useMemo(() => {
    if (!selectedDate) return null;
    return deals.find(d => `${d.date}_${d.accountType}` === selectedDate);
  }, [selectedDate, deals]);

  // Reset page when date changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate]);

  const handleBack = () => {
    setSelectedDate(null);
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Paginated trades for the detailed view
  const paginatedTrades = useMemo(() => {
    if (!selectedDeal) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return selectedDeal.trades.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [selectedDeal, currentPage]);

  const totalPages = selectedDeal ? Math.ceil(selectedDeal.trades.length / ITEMS_PER_PAGE) : 0;

  // Pagination Controls Component
  const Pagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-4 mt-8 pb-4">
        <button 
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(prev => prev - 1)}
          className="w-10 h-10 bg-white/5 border border-white/5 rounded-lg flex items-center justify-center text-white/20 hover:text-white hover:border-white/20 disabled:opacity-10 transition-all active:scale-90"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <div className="flex flex-col items-center">
          <span className="text-[7px] font-black text-white/10 uppercase tracking-[0.5em] mb-1">Sequence</span>
          <span className="text-base font-black text-white tracking-tighter">{currentPage} <span className="text-white/10 mx-2">/</span> {totalPages}</span>
        </div>
        <button 
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(prev => prev + 1)}
          className="w-10 h-10 bg-white/5 border border-white/5 rounded-lg flex items-center justify-center text-white/20 hover:text-white hover:border-white/20 disabled:opacity-10 transition-all active:scale-90"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>
    );
  };

  // Detailed trade list for a specific date
  if (selectedDate && selectedDeal) {
    return (
      <div className="animate-in slide-in-from-right duration-500">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="w-7 h-7 border border-white/5 rounded-lg flex items-center justify-center hover:bg-white/5 hover:border-white/10 transition-all group"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="group-hover:-translate-x-1 transition-transform">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </button>
            <div>
              <h1 className="text-base md:text-lg font-black uppercase tracking-tighter text-white leading-none">History: {selectedDeal.tradesCount}_Cycles</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-[6px] text-white/20 font-black uppercase tracking-[0.3em]">{formatDate(selectedDeal.date)}</p>
                <span className={`px-1.5 py-0.5 text-[5px] font-black uppercase rounded-md ${selectedDeal.accountType === AccountType.LIVE ? 'text-[#FF4D4D] bg-[#FF4D4D]/10' : 'text-[#AEB877] bg-[#AEB877]/10'}`}>
                   {selectedDeal.accountType}_MODE
                </span>
              </div>
            </div>
          </div>
          
          <div className="glass-card px-2.5 py-1.5 shadow-2xl">
            <div className="text-right">
              <p className="text-[6px] font-black text-white/20 uppercase tracking-[0.4em] mb-0.5">Aggregate_ROI</p>
              <p className={`text-base font-black tabular-nums tracking-tighter ${selectedDeal.totalProfit >= 0 ? 'text-[#AEB877] glow-text' : 'text-[#FF4D4D]'}`}>
                {selectedDeal.totalProfit < 0 ? '-' : '+'}${Math.abs(selectedDeal.totalProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-1 bg-[#AEB877] rounded-full shadow-[0_0_10px_#AEB877] animate-pulse"></div>
              <span className="text-[7px] font-black text-white/20 uppercase tracking-[0.5em]">Network_Ledger_Stream</span>
            </div>
            <span className="text-[7px] font-black text-white/10 uppercase tracking-widest">Records {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, selectedDeal.trades.length)} of {selectedDeal.trades.length}</span>
          </div>
          
          {paginatedTrades.map((trade) => (
            <div key={trade.id} className="glass-card p-2.5 md:p-3 flex items-center justify-between gap-3 group hover:border-white/10 transition-all animate-in fade-in slide-in-from-bottom-4 duration-500 relative overflow-hidden">
              <div className="flex items-center gap-3 overflow-hidden relative z-10">
                <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center font-black transition-all border ${
                  trade.type === 'BUY' ? 'bg-[#AEB877]/10 text-[#AEB877] border-[#AEB877]/20' : 'bg-[#FF4D4D]/10 text-[#FF4D4D] border-[#FF4D4D]/20'
                }`}>
                  <span className="text-[6px] leading-none mb-1">{trade.type}</span>
                  <div className="w-3 h-px bg-current opacity-20"></div>
                </div>
                <div className="overflow-hidden">
                  <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                    <span className="text-[6px] bg-white/5 text-white/40 px-1.5 py-0.5 font-black uppercase tracking-[0.2em] rounded-md">{trade.accountType}</span>
                    <span className="text-sm font-black text-white tracking-tighter truncate uppercase">{trade.pair}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-[8px] text-white/20 font-black">{new Date(trade.timestamp).toLocaleTimeString([], { hour12: false })}</p>
                    <span className="w-1 h-1 bg-white/5 rounded-full"></span>
                    <p className="text-[8px] text-white/20 font-black uppercase tracking-[0.3em]">{trade.broker}</p>
                  </div>
                </div>
              </div>
              
              <div className="text-right shrink-0 relative z-10">
                <p className={`font-black text-base md:text-lg tabular-nums tracking-tighter ${trade.profit >= 0 ? 'text-[#AEB877]' : 'text-[#FF4D4D]'}`}>
                  {trade.profit < 0 ? '-' : '+'}${Math.abs(trade.profit).toFixed(2)}
                </p>
                <div className="flex items-center justify-end gap-1 mt-0.5">
                   <div className="w-1 h-1 bg-[#AEB877] rounded-full shadow-[0_0_10px_#AEB877]"></div>
                   <p className="text-[7px] font-black text-white/10 uppercase tracking-[0.3em]">Completed</p>
                </div>
              </div>
            </div>
          ))}

          <Pagination />
        </div>
      </div>
    );
  }

  // Main Daily Deal History List
  return (
    <div className="max-w-3xl mx-auto animate-in fade-in duration-500 pb-16">
      <div className="mb-4">
        <h1 className="text-lg md:text-2xl font-black uppercase tracking-tighter text-white mb-0.5">History: Deals</h1>
        <div className="flex items-center gap-1.5">
           <div className="w-1 h-1 bg-[#AEB877] rounded-full shadow-[0_0_10px_#AEB877] animate-pulse"></div>
           <p className="text-[6px] text-white/20 font-black uppercase tracking-[0.5em]">Status: Daily Trade Sessions</p>
        </div>
      </div>

      <div className="space-y-2">
        {deals.length === 0 ? (
          <div className="glass-card p-10 text-center relative overflow-hidden">
            <div className="w-10 h-10 bg-white/5 rounded-[16px] flex items-center justify-center mx-auto mb-3 text-white/10 shadow-inner border border-white/5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </div>
            <p className="text-white/20 font-black uppercase tracking-[0.6em] text-[7px]">No Trades Found</p>
            <p className="text-[6px] text-white/10 font-black uppercase mt-1.5 tracking-[0.3em]">Initialize robot to begin recording daily deals.</p>
          </div>
        ) : (
          deals.map((deal) => (
            <div key={`${deal.date}_${deal.accountType}`} className="glass-card p-3 md:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 group hover:bg-white/[0.03] transition-all relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#AEB877]/[0.03] rounded-full blur-[60px] -mr-24 -mt-24 group-hover:bg-[#AEB877]/[0.05] transition-colors"></div>
              
              <div className="flex items-center gap-3 relative z-10">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-[16px] border flex flex-col items-center justify-center shadow-2xl ${deal.accountType === AccountType.LIVE ? 'bg-[#FF4D4D]/10 border-[#FF4D4D]/20 text-[#FF4D4D]' : 'bg-[#AEB877]/10 border-[#AEB877]/20 text-[#AEB877]'}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <span className="text-[5px] font-black uppercase tracking-[0.3em] mt-0.5 opacity-40">Verified</span>
                </div>
                
                <div>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`text-[6px] px-1.5 py-0.5 font-black uppercase tracking-[0.2em] rounded-md ${deal.accountType === AccountType.LIVE ? 'bg-[#FF4D4D]/10 text-[#FF4D4D]' : 'bg-[#AEB877]/10 text-[#AEB877]'}`}>
                      {deal.accountType}_SUMMARY
                    </span>
                    <p className="text-[6px] font-black text-white/20 uppercase tracking-[0.3em]">{formatDate(deal.date)}</p>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <p className="font-black text-base md:text-xl text-white tracking-tighter tabular-nums">${deal.trades[0]?.amount || '0'}.00</p>
                    <span className="text-[6px] font-black text-white/10 uppercase tracking-[0.5em]">Principal</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-0 border-white/5 pt-2 sm:pt-0 relative z-10">
                <div className="text-right">
                  <p className="text-[6px] font-black text-white/20 uppercase tracking-[0.4em] mb-0.5">Accumulated_Yield</p>
                  <p className={`text-base md:text-xl font-black tabular-nums tracking-tighter ${deal.totalProfit >= 0 ? 'text-[#AEB877] glow-text' : 'text-[#FF4D4D]'}`}>
                    {deal.totalProfit < 0 ? '-' : '+'}${Math.abs(deal.totalProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                
                <button
                  onClick={() => setSelectedDate(`${deal.date}_${deal.accountType}`)}
                  className="bg-white text-black px-3 py-1.5 rounded-lg font-black uppercase tracking-[0.4em] text-[7px] flex items-center gap-1.5 hover:bg-[#AEB877] transition-all shadow-2xl active:scale-[0.98]"
                >
                  Trades
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="m9 18 6-6-6-6"/></svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-8 p-4 glass-card group overflow-hidden relative shadow-2xl">
         <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#AEB877]/[0.05] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-[3000ms]"></div>
         <div className="flex flex-col md:flex-row items-center justify-around gap-4 relative z-10">
            <div className="text-center md:text-left">
               <p className="text-[6px] font-black text-white/10 uppercase tracking-[0.5em] mb-0.5">Historical_Accuracy</p>
               <p className="text-base font-black text-white tracking-tighter">98.24%_SYNCHRONIZED</p>
            </div>
            <div className="w-px h-6 bg-white/5 hidden md:block"></div>
            <div className="text-center md:text-left">
               <p className="text-[6px] font-black text-white/10 uppercase tracking-[0.5em] mb-0.5">Total_Network_Payout</p>
               <p className="text-base font-black text-[#AEB877] tracking-tighter glow-text">
                  ${totalNetworkPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
               </p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default Deals;